// Task Classification Dialogue Blueprint
// This file defines the static statechart for the task subflow (slot filling and review)
// with no actions or I/O. Keep it pure. For each state, declare:
// (1) a stable state id; (2) the allowed typed triggers; (3) the target state per trigger;
// (4) requiredSlots for that state (exactly one primary slot per step where possible); and
// (5) entryEffectKeys (prompt keys) for the effects layer to render.

export type TaskDialogueState =
  | 'processing_options_selection' // Step 1: System processes task and finds matching options
  | 'no_tasks_found' // Step 2a: No tasks returned - user needs to rephrase
  | 'variant_selection' // Step 3: User selects a variant (measurements)
  | 'amount_clarification' // Step 4: User provides amount; work location comes from assignment (opdracht)
  | 'abort_confirmation' // Single abort-confirmation state
  | 'task_completed' // Terminal: Task processing complete
  | 'task_aborted' // Terminal: Task processing aborted

// Typed triggers for compiler-checked transitions
export type TaskTrigger =
  | 'USER_TEXT_PARSED' // User input parsed successfully
  | 'SELECT_OPTION' // User selects a valid task option
  | 'SELECT_VARIANT' // User selects a valid variant
  | 'ENTER_AMOUNT' // User provides amount
  | 'ENTER_LOCATION' // Legacy intent mapping (no dedicated location step in blueprint)
  | 'CONFIRM' // User confirms action
  | 'ABORT' // User wants to abort
  | 'REPHRASE_TASK' // User rephrases task description
  | 'REPHRASE_VARIANT' // User rephrases variant
  | 'INVALID_AMOUNT' // User provides invalid amount
  | 'INVALID_OPTION' // User selects invalid option
  | 'INVALID_VARIANT' // User selects invalid variant
  | 'SOMETHING_ELSE' // User says something else
  | 'NO_TASKS_FOUND' // System indicates no tasks found
  | 'TASK_COMPLETED' // Parent-facing terminal trigger
  | 'TASK_ABORTED' // Parent-facing terminal trigger

// State metadata interface
export interface TaskStateMetadata {
  requiredSlots?: string[]; // Exactly one primary slot per step where possible
  entryEffectKey?: string; // Prompt key for effects layer to render
  isTerminal?: boolean; // Terminal state flag
  entryActionKeys?: string[]; // Entry actions to run when entering this state
}

// Pure static statechart - no actions or I/O
export const TASK_STATE_TRANSITIONS: Record<TaskDialogueState, Partial<Record<TaskTrigger, TaskDialogueState>>> = {
  processing_options_selection: {
    SELECT_OPTION: 'variant_selection',
    INVALID_OPTION: 'abort_confirmation',
    REPHRASE_TASK: 'abort_confirmation',
    ABORT: 'abort_confirmation',
    SOMETHING_ELSE: 'abort_confirmation',
    NO_TASKS_FOUND: 'no_tasks_found',
  },

  no_tasks_found: {
    REPHRASE_TASK: 'processing_options_selection',
    ABORT: 'abort_confirmation',
    SOMETHING_ELSE: 'abort_confirmation',
  },

  variant_selection: {
    SELECT_VARIANT: 'amount_clarification',
    INVALID_VARIANT: 'abort_confirmation',
    REPHRASE_VARIANT: 'abort_confirmation',
    ABORT: 'abort_confirmation',
    SOMETHING_ELSE: 'abort_confirmation',
  },

  amount_clarification: {
    ENTER_AMOUNT: 'task_completed',
    INVALID_AMOUNT: 'abort_confirmation',
    ABORT: 'abort_confirmation',
    SOMETHING_ELSE: 'abort_confirmation',
  },

  abort_confirmation: {
    CONFIRM: 'task_aborted',
    ABORT: 'processing_options_selection',
    REPHRASE_TASK: 'processing_options_selection',
    SOMETHING_ELSE: 'abort_confirmation',
  },

  task_completed: {
    // Terminal state - emits TASK_COMPLETED to parent
  },

  task_aborted: {
    // Terminal state - emits TASK_ABORTED to parent
  },
};

// State metadata for effects layer
export const TASK_STATE_METADATA: Record<TaskDialogueState, TaskStateMetadata> = {
  processing_options_selection: {
    requiredSlots: ['task_description'],
    entryActionKeys: ['ask_options'],
  },

  no_tasks_found: {
    requiredSlots: ['task_description'],
    entryEffectKey: 'no_tasks_found',
  },

  variant_selection: {
    requiredSlots: ['variant'],
    entryActionKeys: ['ask_variant'],
  },

  amount_clarification: {
    requiredSlots: ['amount'],
    entryActionKeys: ['ask_amount'],
  },

  abort_confirmation: {
    requiredSlots: ['confirmation'],
    entryActionKeys: ['confirm_abort'],
  },

  task_completed: {
    requiredSlots: [],
    entryEffectKey: 'task_completed',
    isTerminal: true,
  },

  task_aborted: {
    requiredSlots: [],
    entryEffectKey: 'task_aborted',
    isTerminal: true,
  },
};

// Pure helper functions - no side effects
export function getNextTaskState(currentState: TaskDialogueState, trigger: TaskTrigger): TaskDialogueState | null {
  const transitions = TASK_STATE_TRANSITIONS[currentState];
  return transitions[trigger] || null;
}

export function isValidTaskTransition(currentState: TaskDialogueState, trigger: TaskTrigger): boolean {
  const nextState = getNextTaskState(currentState, trigger);
  return nextState !== null && nextState !== currentState;
}

export function getTaskStateMetadata(state: TaskDialogueState): TaskStateMetadata {
  return TASK_STATE_METADATA[state];
}

export function isTaskStateTerminal(state: TaskDialogueState): boolean {
  return TASK_STATE_METADATA[state].isTerminal || false;
}
